// client/src/components/plataforma/siteInstitucional/ContatosPage.js
import React, { useState, useEffect, useRef } from 'react';
import { useContactInfo } from '../../../hooks/useContactInfo';
import useAlert from '../../../hooks/useAlert';
import { Save, Loader2, Phone, CheckCircle2 } from 'lucide-react';
import PageHeader from '../../layout/PageHeader';
import { lookupCep } from '../../../utils/cepService';

const ContatosPage = () => {
    const { contactInfo, loading, error, updateContactInfo, refetch } = useContactInfo();
    const { setAlert } = useAlert();
    const [formData, setFormData] = useState({
        email: '',
        phone: '',
        googleMapsEmbed: '',
        openingHours: '',
        address: {
            zipCode: '',
            street: '',
            number: '',
            complement: '',
            neighborhood: '',
            city: '',
            state: '',
            country: 'Brasil'
        }
    });
    
    const [isCepLoading, setIsCepLoading] = useState(false);
    const [cepEncontrado, setCepEncontrado] = useState(false);
    const numberRef = useRef(null);

    useEffect(() => {
        if (contactInfo) {
            setFormData({
                email: contactInfo.email || '', 
                phone: contactInfo.phone || '',
                googleMapsEmbed: contactInfo.googleMapsEmbed || '',
                openingHours: contactInfo.openingHours || '',
                address: {
                    zipCode: contactInfo.address?.cep || '', // ✅ Mapeando de 'cep' do backend para 'zipCode' no frontend
                    street: contactInfo.address?.logradouro || '', // ✅ Mapeando de 'logradouro' para 'street'
                    number: contactInfo.address?.numero || '',     // ✅ Mapeando de 'numero' para 'number'
                    complement: contactInfo.address?.complemento || '', // ✅ Mapeando de 'complemento' para 'complement'
                    neighborhood: contactInfo.address?.bairro || '',    // ✅ Mapeando de 'bairro' para 'neighborhood'
                    city: contactInfo.address?.localidade || '',      // ✅ Mapeando de 'localidade' para 'city'
                    state: contactInfo.address?.uf || '',             // ✅ Mapeando de 'uf' para 'state'
                    country: contactInfo.address?.pais || 'Brasil'    // ✅ Mapeando de 'pais' para 'country'
                }
            });
        }
    }, [contactInfo]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleAddressChange = (e) => {
        const { name, value } = e.target;
        if (name === 'zipCode') setCepEncontrado(false);
        setFormData(prev => ({
            ...prev,
            address: {
                ...prev.address,
                [name]: value
            }
        }));
    };
    
    const handleCepBlur = async (e) => {
        const cep = e.target.value.replace(/\D/g, '');
        if (cep.length !== 8) {
            setFormData(prev => ({
                ...prev,
                address: {
                    ...prev.address,
                    street: '',
                    neighborhood: '',
                    city: '',
                    state: ''
                }
            }));
            return;
        }

        setIsCepLoading(true);
        try {
            const addressData = await lookupCep(cep);
            setFormData(prev => ({
                ...prev,
                address: {
                    ...prev.address,
                    zipCode: cep, // Garante que o CEP formatado esteja no estado
                    street: addressData.logradouro || '',
                    neighborhood: addressData.bairro || '',
                    city: addressData.localidade || '',
                    state: addressData.uf || '',
                }
            }));
            setCepEncontrado(true);
            if (numberRef.current) numberRef.current.focus();
        } catch (err) {
            setAlert(err.message, 'error');
            setFormData(prev => ({
                ...prev,
                address: {
                    ...prev.address,
                    street: '',
                    neighborhood: '',
                    city: '',
                    state: ''
                }
            }));
        } finally {
            setIsCepLoading(false);
        }
    };
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                contactInfo: {
                    email: formData.email,
                    phone: formData.phone,
                    googleMapsEmbed: formData.googleMapsEmbed,
                    openingHours: formData.openingHours,
                    address: {
                        street: formData.address.street,
                        number: formData.address.number,
                        complement: formData.address.complement,
                        neighborhood: formData.address.neighborhood,
                        city: formData.address.city,
                        state: formData.address.state,
                        zipCode: formData.address.zipCode,
                        country: formData.address.country
                    }
                }
            };
            await updateContactInfo(payload);
            refetch();
        } catch (err) {
            // Erro já tratado no hook, setAlert já foi chamado lá.
        }
    };

    if (loading && !contactInfo) {
        return <div className="flex justify-center items-center h-64"><Loader2 className="animate-spin h-8 w-8 text-blue-500" /></div>;
    }

    if (error) {
        return <div className="alert alert-danger">{error}</div>;
    }

    return (
        <div className="page-management-container">
            <form onSubmit={handleSubmit}>
                <PageHeader
                    title="Informações de Contato"
                    icon={<Phone className="icon-title" />}
                    actions={
                        <button type="submit" className="action-button action-button-primary" disabled={loading || isCepLoading}>
                            {(loading || isCepLoading) ? <Loader2 size={18} className="icon animate-spin" /> : <Save size={18} className="icon" />} Salvar Alterações
                        </button>
                    }
                />
                <div className="content-panel mt-6">
                    <div className="form-section">
                        <h3 className="form-section-title">Contato Principal</h3>
                        <div className="form-grid-container grid-cols-1 md:grid-cols-2">
                            <div className="form-group">
                                <label htmlFor="email">Email de Contato</label>
                                <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} className="form-input" />
                            </div>
                            <div className="form-group">
                                <label htmlFor="phone">Telefone de Contato</label>
                                <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} className="form-input" />
                            </div>
                            <div className="form-group md:col-span-2">
                                <label htmlFor="openingHours">Horário de Funcionamento</label>
                                <textarea id="openingHours" name="openingHours" value={formData.openingHours} onChange={handleChange} className="form-input" rows="3" placeholder="Ex: Seg-Sex: 9h-18h, Sáb: 9h-13h"></textarea>
                            </div>
                        </div>
                    </div>
                    <div className="form-section mt-8">
                        <h3 className="form-section-title">Endereço Físico</h3>
                        <div className="form-grid-container grid-cols-1 md:grid-cols-3">
                            <div className="form-group">
                                <label htmlFor="zipCode">CEP</label>
                                <div className="flex items-center gap-2">
                                    <input
                                        type="text"
                                        id="zipCode"
                                        name="zipCode"
                                        value={formData.address.zipCode}
                                        onChange={handleAddressChange}
                                        onBlur={handleCepBlur}
                                        maxLength="9"
                                        className="form-input"
                                        placeholder="Ex: 00000-000"
                                    />
                                    {isCepLoading && <Loader2 className="animate-spin text-blue-500" />}
                                </div>
                                {cepEncontrado ? (
                                    <div className="form-hint" style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                                        <CheckCircle2 size={13} style={{ color: 'var(--color-success, #16a34a)', flexShrink: 0 }} />
                                        Endereço preenchido automaticamente.
                                        <button type="button" onClick={() => setCepEncontrado(false)}
                                            style={{ background: 'none', border: 'none', padding: 0, color: 'var(--color-primary)', cursor: 'pointer', fontSize: 'inherit', textDecoration: 'underline' }}>
                                            Editar manualmente
                                        </button>
                                    </div>
                                ) : (
                                    <div className="form-hint">Digite o CEP e aguarde o preenchimento automático</div>
                                )}
                            </div>
                            <div className="form-group md:col-span-2">
                                <label htmlFor="street">Logradouro (Rua, Av.)</label>
                                <input type="text" id="street" name="street" value={formData.address.street} onChange={handleAddressChange} className="form-input"
                                    readOnly={cepEncontrado} style={cepEncontrado ? { backgroundColor: 'var(--color-bg-subtle, #f1f5f9)', cursor: 'not-allowed' } : undefined} />
                            </div>
                            <div className="form-group">
                                <label htmlFor="number">Número</label>
                                <input ref={numberRef} type="text" id="number" name="number" value={formData.address.number} onChange={handleAddressChange} className="form-input" />
                            </div>
                            <div className="form-group">
                                <label htmlFor="complement">Complemento</label>
                                <input type="text" id="complement" name="complement" value={formData.address.complement} onChange={handleAddressChange} className="form-input" />
                            </div>
                            <div className="form-group">
                                <label htmlFor="neighborhood">Bairro</label>
                                <input type="text" id="neighborhood" name="neighborhood" value={formData.address.neighborhood} onChange={handleAddressChange} className="form-input"
                                    readOnly={cepEncontrado} style={cepEncontrado ? { backgroundColor: 'var(--color-bg-subtle, #f1f5f9)', cursor: 'not-allowed' } : undefined} />
                            </div>
                            <div className="form-group">
                                <label htmlFor="city">Cidade</label>
                                <input type="text" id="city" name="city" value={formData.address.city} onChange={handleAddressChange} className="form-input"
                                    readOnly={cepEncontrado} style={cepEncontrado ? { backgroundColor: 'var(--color-bg-subtle, #f1f5f9)', cursor: 'not-allowed' } : undefined} />
                            </div>
                            <div className="form-group">
                                <label htmlFor="state">Estado (UF)</label>
                                <input type="text" id="state" name="state" maxLength="2" value={formData.address.state} onChange={handleAddressChange} className="form-input"
                                    readOnly={cepEncontrado} style={cepEncontrado ? { backgroundColor: 'var(--color-bg-subtle, #f1f5f9)', cursor: 'not-allowed' } : undefined} />
                            </div>
                            <div className="form-group">
                                <label htmlFor="country">País</label>
                                <input type="text" id="country" name="country" value={formData.address.country} onChange={handleAddressChange} className="form-input" />
                            </div>
                             <div className="form-group md:col-span-3">
                                <label htmlFor="googleMapsEmbed">Código de Incorporação Google Maps (iframe)</label>
                                <textarea id="googleMapsEmbed" name="googleMapsEmbed" value={formData.googleMapsEmbed} onChange={handleChange} className="form-input" rows="5" placeholder="Cole o código iframe do Google Maps aqui"></textarea>
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default ContatosPage;